<script lang="ts" setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { useToast } from 'primevue/usetoast'
import axiosInstance from '@/plugins/axios'
import SettingsPageTemplate from '@/components/templates/SettingsPageTemplate.vue'
import { useHelpStore } from '@/store/help'
import { useSessionStore } from '@/store/sessionStore'
import { type ThemeSettingChange, type ThemeSettingRecord, useThemeStore } from '@/store/themeStore'
import { isValidColor, isValidLink, orderCategories } from './layoutHelper'

const route = useRoute()
const router = useRouter()
const toast = useToast()
const helpStore = useHelpStore()
const sessionStore = useSessionStore()
const themeStore = useThemeStore()

const assetsUrl = String(import.meta.env.VITE_ADMIN_ASSETS_URL ?? '')
const isReviewDrawerOpen = ref(false)
const selectedSettingKeys = ref<string[]>([])
const lastInvalidColorKey = ref<string | null>(null)
const lastInvalidColorTimestamp = ref(0)

const isAdmin = computed(() => sessionStore.user?.role === 'ai_lab_admin')
const tenantName = computed(() => sessionStore.tenant?.name ?? 'Tenant')
const isLoading = computed(() => themeStore.loading && !themeStore.isLoaded)
const pageError = computed(() => themeStore.error)

const visibleTemplateSettings = computed<ThemeSettingRecord[]>(() =>
  themeStore.themeTemplate.filter((setting) => !setting.hidden || isAdmin.value)
)

const visibleSettingKeySet = computed(
  () => new Set(visibleTemplateSettings.value.map((setting) => setting.key))
)

const moduleCategories = computed(() => {
  const categories = new Set<string>()

  for (const setting of visibleTemplateSettings.value) {
    if (setting.isModuleSetting && setting.category) {
      categories.add(setting.category)
    }
  }

  return [...categories]
})

const groupedSettings = computed<Record<string, ThemeSettingRecord[]>>(() => {
  const grouped: Record<string, ThemeSettingRecord[]> = {}

  for (const setting of visibleTemplateSettings.value) {
    const category = setting.category || 'Uncategorized'
    if (!grouped[category]) {
      grouped[category] = []
    }

    grouped[category].push(setting)
  }

  for (const category of Object.keys(grouped)) {
    grouped[category].sort((left, right) => (left.order ?? 0) - (right.order ?? 0))
  }

  return grouped
})

const orderedCategories = computed(() =>
  orderCategories(Object.keys(groupedSettings.value), moduleCategories.value)
)

const currentCategory = computed(() => {
  const section =
    typeof route.query.section === 'string'
      ? route.query.section
      : Array.isArray(route.query.section)
        ? route.query.section[0]
        : null

  if (section && orderedCategories.value.includes(section)) {
    return section
  }

  return orderedCategories.value[0] ?? null
})

const currentSettings = computed(() =>
  currentCategory.value ? (groupedSettings.value[currentCategory.value] ?? []) : []
)

const invalidColorKeys = computed(() => {
  const keys = new Set<string>()

  for (const setting of visibleTemplateSettings.value) {
    if (setting.fieldType !== 'color') {
      continue
    }

    const value = themeStore.getSettingValue(setting.key)
    if (typeof value === 'string' && value.trim() && !isValidColor(value.trim())) {
      keys.add(setting.key)
    }
  }

  return keys
})

const invalidLinkKeys = computed(() => {
  const keys = new Set<string>()

  for (const setting of visibleTemplateSettings.value) {
    if (setting.fieldType !== 'link') {
      continue
    }

    const value = themeStore.getSettingValue(setting.key)
    if (typeof value === 'string' && value.trim() && !isValidLink(value.trim())) {
      keys.add(setting.key)
    }
  }

  return keys
})

const changedSettings = computed<ThemeSettingChange[]>(() =>
  themeStore.changedSettings.filter((change) => visibleSettingKeySet.value.has(change.key))
)

const changedSettingsByCategory = computed<Record<string, ThemeSettingChange[]>>(() => {
  const grouped: Record<string, ThemeSettingChange[]> = {}

  for (const change of changedSettings.value) {
    if (!grouped[change.category]) {
      grouped[change.category] = []
    }

    grouped[change.category].push(change)
  }

  return grouped
})

const reviewCategories = computed(() => {
  const changedCategorySet = new Set(Object.keys(changedSettingsByCategory.value))
  const ordered = orderedCategories.value.filter((category) => changedCategorySet.has(category))
  const additional = Object.keys(changedSettingsByCategory.value)
    .filter((category) => !ordered.includes(category))
    .sort((left, right) => left.localeCompare(right))

  return [...ordered, ...additional]
})

const availableFonts = computed(() => themeStore.availableFonts)
const boldFonts = computed(() => ['Automatic Bold Styling', ...themeStore.availableFonts])

const lastUpdatedLabel = computed(() => {
  const source = themeStore.lastSyncedAt ?? themeStore.themeUpdatedAt ?? themeStore.lastLoadedAt
  if (!source) {
    return 'Not synced yet'
  }

  return new Date(source).toLocaleString('de-DE', {
    dateStyle: 'short',
    timeStyle: 'short'
  })
})

const syncStatusLabel = computed(() => {
  switch (themeStore.syncStatus) {
    case 'saving':
      return 'Saving'
    case 'loading':
      return 'Loading'
    case 'error':
      return 'Error'
    case 'out_of_sync':
      return 'Draft changes'
    case 'in_sync':
      return 'In sync'
    default:
      return 'Idle'
  }
})

const saveDisabled = computed(
  () =>
    themeStore.saving ||
    !themeStore.hasUnsavedChanges ||
    invalidColorKeys.value.size > 0 ||
    invalidLinkKeys.value.size > 0
)

const resolveAssetPath = (path: unknown) => {
  if (typeof path !== 'string' || !path.trim()) {
    return undefined
  }

  if (/^(https?:)?\/\//.test(path) || path.startsWith('data:')) {
    return path
  }

  return `${assetsUrl}${path}`
}

const getSettingTitle = (setting: ThemeSettingRecord) =>
  String(setting.title || themeStore.getSetting(setting.key)?.title || setting.key)

const getSettingValue = (key: string) => themeStore.getSettingValue(key)

const getCardClasses = (setting: ThemeSettingRecord) => ({
  'layout-setting-card--selected': selectedSettingKeys.value.includes(setting.key),
  'layout-setting-card--new':
    !selectedSettingKeys.value.includes(setting.key) &&
    !!setting.createdAt &&
    new Date(setting.createdAt).getTime() > Date.now() - 7 * 24 * 60 * 60 * 1000 &&
    (themeStore.getSettingValue(setting.key) === null ||
      themeStore.getSettingValue(setting.key) === undefined),
  'layout-setting-card--invalid':
    invalidColorKeys.value.has(setting.key) || invalidLinkKeys.value.has(setting.key)
})

const formatChangeValue = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return 'Empty'
  }

  if (typeof value === 'boolean') {
    return value ? 'Enabled' : 'Disabled'
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(', ') : 'Empty'
  }

  if (typeof value === 'object') {
    return JSON.stringify(value, null, 2)
  }

  return String(value)
}

const setActiveCategory = async (category: string) => {
  await router.replace({
    query: {
      ...route.query,
      section: category
    }
  })
}

const syncQuerySection = async () => {
  if (!orderedCategories.value.length) {
    return
  }

  const querySection =
    typeof route.query.section === 'string'
      ? route.query.section
      : Array.isArray(route.query.section)
        ? route.query.section[0]
        : null

  if (!querySection || !orderedCategories.value.includes(querySection)) {
    await router.replace({
      query: {
        ...route.query,
        section: orderedCategories.value[0]
      }
    })
  }
}

const syncHighlightedSettingsFromUrl = async () => {
  if (!themeStore.isLoaded) {
    return
  }

  const rawSettingQuery =
    typeof route.query.setting === 'string'
      ? route.query.setting
      : Array.isArray(route.query.setting)
        ? route.query.setting[0]
        : null

  if (!rawSettingQuery) {
    selectedSettingKeys.value = []
    return
  }

  const validKeys = rawSettingQuery
    .split(',')
    .map((key) => key.trim())
    .filter((key) => visibleSettingKeySet.value.has(key))

  selectedSettingKeys.value = validKeys

  if (!validKeys.length) {
    return
  }

  const targetCategory = orderedCategories.value.find((category) =>
    groupedSettings.value[category]?.some((setting) => setting.key === validKeys[0])
  )

  if (targetCategory && currentCategory.value !== targetCategory) {
    await setActiveCategory(targetCategory)
  }

  await nextTick()

  const targetElement = document.querySelector('.layout-setting-card--selected')
  if (targetElement instanceof HTMLElement) {
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
  }
}

const updateSettingValue = (key: string, value: unknown) => {
  themeStore.setThemeValue(key, value)
}

const notifyInvalidColor = (key: string) => {
  if (!invalidColorKeys.value.has(key)) {
    lastInvalidColorKey.value = null
    return
  }

  const now = Date.now()
  if (lastInvalidColorKey.value === key && now - lastInvalidColorTimestamp.value < 3000) {
    return
  }

  toast.add({
    severity: 'error',
    summary: 'Invalid color',
    detail: 'Please use an RGB(A) or hexadecimal value.',
    life: 3000
  })
  lastInvalidColorKey.value = key
  lastInvalidColorTimestamp.value = now
}

const notifyInvalidLink = (key: string) => {
  if (!invalidLinkKeys.value.has(key)) {
    return
  }

  toast.add({
    severity: 'error',
    summary: 'Invalid link',
    detail: 'Please ensure the URL starts with https:// or http://.',
    life: 3000
  })
}

const openSettingHelp = (setting: ThemeSettingRecord) => {
  const content = {
    title: getSettingTitle(setting),
    helpText: setting.help,
    imgSrc: resolveAssetPath(setting.helpImage)
  }

  if (!content.title && !content.helpText && !content.imgSrc) {
    return
  }

  helpStore.toggle(`layout:${setting.key}`, content)
}

const openChangeReview = () => {
  if (saveDisabled.value) {
    return
  }

  isReviewDrawerOpen.value = true
}

const closeChangeReview = () => {
  isReviewDrawerOpen.value = false
}

const resetChanges = () => {
  themeStore.resetToDatabaseState()
  closeChangeReview()
}

const saveTheme = async () => {
  if (saveDisabled.value) {
    return
  }

  try {
    await themeStore.saveTheme()
    closeChangeReview()
    toast.add({
      severity: 'success',
      summary: 'Theme saved',
      detail: 'The tenant layout settings have been saved successfully.',
      life: 3000
    })
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: 'Save failed',
      detail: error?.response?.data?.message || error?.message || 'Failed to save the theme.',
      life: 4000
    })
  }
}

const onFileSelect = async (event: { files?: File[] }, key: string) => {
  const file = event.files?.[0]
  if (!file) {
    return
  }

  const formData = new FormData()
  formData.append('file', file)

  try {
    const response = await axiosInstance.post('/theme/uploadFile', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })

    updateSettingValue(key, response.data.path)
    toast.add({
      severity: 'success',
      summary: 'Upload complete',
      detail: 'The file has been uploaded successfully.',
      life: 2500
    })
  } catch (error: any) {
    toast.add({
      severity: 'error',
      summary: 'Upload failed',
      detail: error?.response?.data?.message || 'The file could not be uploaded.',
      life: 4000
    })
  }
}

watch(
  orderedCategories,
  () => {
    void syncQuerySection()
  },
  { immediate: true }
)

watch(
  [() => route.query.setting, orderedCategories, () => themeStore.isLoaded],
  () => {
    void syncHighlightedSettingsFromUrl()
  },
  { immediate: true }
)

watch(
  () => route.query.section,
  () => {
    if (isReviewDrawerOpen.value) {
      closeChangeReview()
    }
  }
)

onMounted(async () => {
  if (!themeStore.isLoaded) {
    try {
      await themeStore.initialize()
    } catch (error) {
      console.error('Failed to initialize theme store for layout overview:', error)
    }
  }
})

onBeforeUnmount(() => {
  if (helpStore.activeKey?.startsWith('layout:')) {
    helpStore.close()
  }
})
</script>

<template>
  <SettingsPageTemplate
    use-header-actions
    cancel-label="Reset"
    save-label="Review Changes"
    :dirty="themeStore.hasUnsavedChanges"
    :loading="themeStore.saving"
    :save-disabled="saveDisabled"
    :cancel-disabled="themeStore.saving || !themeStore.hasUnsavedChanges"
    :cancel-navigates-back="false"
    @save="openChangeReview"
    @cancel="resetChanges"
  >
    <template #top-content>
      <div class="layout-overview-top">
        <section class="layout-status-strip">
          <article class="layout-status-card">
            <span class="layout-status-label">Theme status</span>
            <strong class="layout-status-value">{{ syncStatusLabel }}</strong>
            <span class="layout-status-meta">
              {{
                themeStore.hasUnsavedChanges
                  ? `${changedSettings.length} pending change${changedSettings.length === 1 ? '' : 's'}`
                  : 'No local changes'
              }}
            </span>
          </article>

          <article class="layout-status-card">
            <span class="layout-status-label">Theme</span>
            <strong class="layout-status-value">{{ themeStore.themeName ?? tenantName }}</strong>
            <span class="layout-status-meta">Last sync: {{ lastUpdatedLabel }}</span>
          </article>

          <div class="layout-status-actions">
            <Button
              label="Review Changes"
              severity="secondary"
              outlined
              :disabled="!themeStore.hasUnsavedChanges"
              @click="openChangeReview"
            >
              <template #icon>
                <FontAwesomeIcon :icon="['fas', 'sliders']" />
              </template>
            </Button>
          </div>
        </section>

        <nav v-if="orderedCategories.length" class="layout-tabs" aria-label="Layout categories">
          <button
            v-for="category in orderedCategories"
            :key="category"
            type="button"
            class="layout-tab"
            :class="{ 'layout-tab--active': currentCategory === category }"
            @click="setActiveCategory(category)"
          >
            {{ category }}
          </button>
        </nav>
      </div>
    </template>

    <template #settings-content>
      <div v-if="isLoading" class="layout-placeholder">
        <Skeleton height="2.75rem" class="mb-3" />
        <Skeleton height="10rem" class="mb-3" />
        <Skeleton height="10rem" />
      </div>

      <Message v-else-if="pageError" severity="error" class="layout-message">
        {{ pageError }}
      </Message>

      <Message v-else-if="!orderedCategories.length" severity="secondary" class="layout-message">
        No layout settings are available for the current tenant.
      </Message>

      <div
        v-else
        class="layout-editor-shell"
        :class="{ 'layout-editor-shell--review-open': isReviewDrawerOpen }"
      >
        <div class="layout-editor-main">
          <section class="layout-section-card">
            <header class="layout-section-header">
              <div>
                <h2 class="layout-section-title">{{ currentCategory }}</h2>
                <p class="layout-section-copy">
                  Configure the appearance and content settings for this layout section.
                </p>
              </div>
            </header>

            <div class="layout-settings-grid">
              <article
                v-for="setting in currentSettings"
                :key="setting.key"
                class="layout-setting-card"
                :class="getCardClasses(setting)"
              >
                <header class="layout-setting-header">
                  <div class="layout-setting-heading">
                    <h3 class="layout-setting-title">{{ getSettingTitle(setting) }}</h3>
                    <span class="layout-setting-key">{{ setting.key }}</span>
                  </div>

                  <button
                    v-if="setting.help || setting.helpImage"
                    type="button"
                    class="layout-setting-help"
                    @click="openSettingHelp(setting)"
                  >
                    <FontAwesomeIcon :icon="['fas', 'circle-question']" />
                    <span>Help</span>
                  </button>
                </header>

                <div class="layout-setting-control">
                  <template v-if="setting.fieldType === 'color'">
                    <InputText
                      :model-value="getSettingValue(setting.key)"
                      class="w-full"
                      :invalid="invalidColorKeys.has(setting.key)"
                      @update:model-value="updateSettingValue(setting.key, $event)"
                      @blur="notifyInvalidColor(setting.key)"
                    />
                  </template>

                  <template v-else-if="setting.fieldType === 'font'">
                    <Select
                      :model-value="getSettingValue(setting.key)"
                      class="w-full"
                      :options="setting.key === 'boldFont' ? boldFonts : availableFonts"
                      @update:model-value="updateSettingValue(setting.key, $event)"
                    />
                  </template>

                  <template v-else-if="setting.fieldType === 'link'">
                    <InputText
                      :model-value="getSettingValue(setting.key)"
                      class="w-full"
                      :invalid="invalidLinkKeys.has(setting.key)"
                      @update:model-value="updateSettingValue(setting.key, $event)"
                      @blur="notifyInvalidLink(setting.key)"
                    />
                  </template>

                  <template v-else-if="setting.fieldType === 'checkbox'">
                    <ToggleButton
                      :model-value="Boolean(getSettingValue(setting.key))"
                      :true-value="true"
                      :false-value="false"
                      class="w-full"
                      on-label="Enabled"
                      off-label="Disabled"
                      @update:model-value="updateSettingValue(setting.key, $event)"
                    />
                  </template>

                  <template v-else-if="setting.fieldType === 'file'">
                    <div class="layout-file-field">
                      <div class="layout-file-actions">
                        <FileUpload
                          mode="basic"
                          custom-upload
                          auto
                          choose-label="Upload"
                          severity="secondary"
                          @select="onFileSelect($event, setting.key)"
                        />
                        <Button
                          v-if="getSettingValue(setting.key)"
                          label="Remove"
                          severity="secondary"
                          outlined
                          @click="updateSettingValue(setting.key, '')"
                        >
                          <template #icon>
                            <FontAwesomeIcon :icon="['fas', 'trash']" />
                          </template>
                        </Button>
                      </div>

                      <Image
                        v-if="getSettingValue(setting.key)"
                        :src="resolveAssetPath(getSettingValue(setting.key))"
                        alt="Theme asset"
                        width="120"
                        preview
                      />
                    </div>
                  </template>

                  <template v-else-if="setting.fieldType === 'translation'">
                    <LimitedEditor
                      :model-value="getSettingValue(setting.key)"
                      editor-style="height: 220px"
                      @update:model-value="updateSettingValue(setting.key, $event)"
                    />
                  </template>

                  <template v-else-if="setting.fieldType === 'predefinedSelect'">
                    <Select
                      :model-value="getSettingValue(setting.key)"
                      :options="setting.options || []"
                      option-label="name"
                      option-value="value"
                      class="w-full"
                      @update:model-value="updateSettingValue(setting.key, $event)"
                    />
                  </template>

                  <template v-else-if="setting.key === 'pdfContactInformation'">
                    <LimitedEditor
                      :model-value="getSettingValue(setting.key)"
                      editor-style="height: 220px"
                      @update:model-value="updateSettingValue(setting.key, $event)"
                    />
                  </template>

                  <template v-else>
                    <InputText
                      :model-value="getSettingValue(setting.key)"
                      class="w-full"
                      @update:model-value="updateSettingValue(setting.key, $event)"
                    />
                  </template>
                </div>

                <p v-if="setting.help" class="layout-setting-copy">
                  {{ setting.help }}
                </p>

                <Message
                  v-if="invalidColorKeys.has(setting.key)"
                  size="small"
                  severity="error"
                  variant="simple"
                >
                  Invalid color format. Please use an RGB(A) or hexadecimal value.
                </Message>

                <Message
                  v-if="invalidLinkKeys.has(setting.key)"
                  size="small"
                  severity="error"
                  variant="simple"
                >
                  Invalid link format. Please use a valid http:// or https:// URL.
                </Message>
              </article>
            </div>
          </section>

          <button
            v-if="isReviewDrawerOpen"
            type="button"
            class="layout-review-backdrop"
            aria-label="Close review panel"
            @click="closeChangeReview"
          />
        </div>

        <aside
          class="layout-review-drawer"
          :class="{ 'layout-review-drawer--open': isReviewDrawerOpen }"
        >
          <header class="layout-review-header">
            <div>
              <span class="layout-review-eyebrow">Before saving</span>
              <h2 class="layout-review-title">Pending changes</h2>
            </div>
            <button
              type="button"
              class="layout-review-close"
              aria-label="Close review panel"
              @click="closeChangeReview"
            >
              <FontAwesomeIcon :icon="['fas', 'xmark']" />
            </button>
          </header>

          <div class="layout-review-body">
            <p class="layout-review-copy">
              Review the local changes before they are written back to the tenant theme.
            </p>

            <div v-if="changedSettings.length" class="layout-review-groups">
              <section
                v-for="category in reviewCategories"
                :key="category"
                class="layout-review-group"
              >
                <h3 class="layout-review-group-title">{{ category }}</h3>

                <article
                  v-for="change in changedSettingsByCategory[category]"
                  :key="change.key"
                  class="layout-review-item"
                >
                  <header class="layout-review-item-header">
                    <strong>{{ change.title }}</strong>
                    <span>{{ change.key }}</span>
                  </header>

                  <div class="layout-review-values">
                    <div class="layout-review-value-block">
                      <span class="layout-review-value-label">Before</span>
                      <pre class="layout-review-value">{{
                        formatChangeValue(change.previousValue)
                      }}</pre>
                    </div>

                    <div class="layout-review-value-block">
                      <span class="layout-review-value-label">After</span>
                      <pre class="layout-review-value">{{
                        formatChangeValue(change.nextValue)
                      }}</pre>
                    </div>
                  </div>
                </article>
              </section>
            </div>

            <Message v-else severity="secondary" variant="simple">
              There are no pending changes to review.
            </Message>
          </div>

          <footer class="layout-review-footer">
            <Button label="Close" severity="secondary" outlined @click="closeChangeReview" />
            <Button
              label="Save Theme"
              :loading="themeStore.saving"
              :disabled="saveDisabled"
              @click="saveTheme"
            >
              <template #icon>
                <FontAwesomeIcon :icon="['fas', 'floppy-disk']" />
              </template>
            </Button>
          </footer>
        </aside>
      </div>
    </template>
  </SettingsPageTemplate>
</template>

<style scoped>
.layout-overview-top {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.layout-status-strip {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr)) auto;
  gap: 1rem;
  align-items: stretch;
}

.layout-status-card {
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  padding: 1rem 1.1rem;
  border: 1px solid #ece9e3;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfbf8 100%);
}

.layout-status-label {
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
}

.layout-status-value {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;
}

.layout-status-meta {
  font-size: 0.82rem;
  color: #6b7280;
}

.layout-status-actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
}

.layout-tabs {
  display: flex;
  gap: 0;
  padding: 0;
  border-bottom: 1px solid #f0efea;
  overflow-x: auto;
}

.layout-tab {
  display: inline-flex;
  align-items: center;
  min-height: 2.8rem;
  padding: 0.7rem 1.1rem;
  border: none;
  border-bottom: 2px solid transparent;
  background: transparent;
  color: #6b6e75;
  font-size: 0.875rem;
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  transition:
    color 0.15s ease,
    border-color 0.15s ease;
}

.layout-tab:hover {
  color: var(--tuev-dark-blue, #002864);
}

.layout-tab--active {
  color: var(--tuev-dark-blue, #002864);
  border-bottom-color: var(--tuev-dark-blue, #002864);
  font-weight: 600;
}

.layout-placeholder,
.layout-message {
  margin-top: 0.25rem;
}

.layout-editor-shell {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 0;
  gap: 0;
  min-height: 0;
  transition: grid-template-columns 0.25s ease;
}

.layout-editor-shell--review-open {
  grid-template-columns: minmax(0, 1fr) minmax(22rem, 24rem);
  gap: 1rem;
}

.layout-editor-main {
  position: relative;
  min-width: 0;
}

.layout-section-card {
  position: relative;
  z-index: 1;
  padding: 1.25rem;
  border: 1px solid #ece9e3;
  border-radius: 16px;
  background: #ffffff;
  transition:
    transform 0.25s ease,
    filter 0.25s ease;
}

.layout-editor-shell--review-open .layout-section-card {
  transform: translateX(-0.25rem);
  filter: saturate(0.92);
}

.layout-section-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1.15rem;
}

.layout-section-title {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #111827;
}

.layout-section-copy {
  margin: 0.2rem 0 0;
  font-size: 0.84rem;
  color: #6b7280;
}

.layout-settings-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1rem;
}

.layout-setting-card {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  padding: 1rem;
  min-height: 100%;
  border: 1px solid #ece9e3;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfbf8 100%);
  box-shadow:
    0 1px 2px rgba(15, 17, 21, 0.03),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    transform 0.15s ease;
}

.layout-setting-card:hover {
  transform: translateY(-1px);
  border-color: #ddd8cf;
  box-shadow:
    0 4px 10px rgba(15, 17, 21, 0.05),
    inset 0 1px 0 rgba(255, 255, 255, 0.9);
}

.layout-setting-card--selected {
  border-color: rgba(0, 220, 230, 0.7);
  box-shadow:
    0 0 0 1px rgba(0, 220, 230, 0.28),
    0 6px 18px rgba(0, 220, 230, 0.08);
}

.layout-setting-card--new {
  border-color: rgba(0, 220, 230, 0.55);
}

.layout-setting-card--invalid {
  border-color: rgba(239, 68, 68, 0.35);
}

.layout-setting-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.layout-setting-heading {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.layout-setting-title {
  margin: 0;
  font-size: 0.95rem;
  font-weight: 650;
  color: #111827;
}

.layout-setting-key {
  font-size: 0.75rem;
  color: #9ca3af;
  word-break: break-all;
}

.layout-setting-help {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  min-height: 2rem;
  padding: 0.3rem 0.65rem;
  border: 1px solid #ece9e3;
  border-radius: 999px;
  background: #ffffff;
  color: #6b7280;
  font-size: 0.76rem;
  font-weight: 600;
  cursor: pointer;
  transition:
    color 0.15s ease,
    border-color 0.15s ease,
    background 0.15s ease;
}

.layout-setting-help:hover {
  color: var(--tuev-dark-blue, #002864);
  border-color: rgba(0, 40, 100, 0.14);
  background: #f9fafb;
}

.layout-setting-control {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.layout-setting-copy {
  margin: 0;
  font-size: 0.82rem;
  line-height: 1.55;
  color: #6b7280;
}

.layout-file-field {
  display: flex;
  flex-direction: column;
  gap: 0.85rem;
}

.layout-file-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.65rem;
  align-items: center;
}

.layout-review-backdrop {
  position: absolute;
  inset: 0;
  z-index: 2;
  border: none;
  border-radius: 16px;
  background: rgba(247, 247, 245, 0.68);
  backdrop-filter: blur(1.5px);
  cursor: pointer;
}

.layout-review-drawer {
  width: 0;
  max-width: 0;
  opacity: 0;
  overflow: hidden;
  border: 1px solid transparent;
  border-radius: 18px;
  background:
    linear-gradient(180deg, rgba(0, 220, 230, 0.08) 0%, rgba(255, 255, 255, 0.98) 18%), #ffffff;
  box-shadow: 0 1px 2px rgba(15, 17, 21, 0.04);
  transform: translateX(1rem);
  transition:
    width 0.25s ease,
    max-width 0.25s ease,
    opacity 0.25s ease,
    transform 0.25s ease,
    border-color 0.25s ease;
}

.layout-review-drawer--open {
  width: 100%;
  max-width: 24rem;
  opacity: 1;
  border-color: #e8e6e0;
  transform: translateX(0);
}

.layout-review-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1rem 0.85rem;
  border-bottom: 1px solid rgba(0, 40, 100, 0.08);
}

.layout-review-eyebrow {
  display: inline-flex;
  align-items: center;
  min-height: 1.65rem;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background: rgba(0, 220, 230, 0.12);
  color: #066a78;
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.layout-review-title {
  margin: 0.55rem 0 0;
  font-size: 1.02rem;
  font-weight: 700;
  color: var(--tuev-dark-blue, #002864);
}

.layout-review-close {
  width: 2rem;
  height: 2rem;
  display: grid;
  place-items: center;
  border: 1px solid transparent;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.78);
  color: var(--tuev-dark-blue, #002864);
  cursor: pointer;
}

.layout-review-body {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
  max-height: calc(100vh - 16rem);
  overflow: auto;
}

.layout-review-copy {
  margin: 0;
  font-size: 0.88rem;
  line-height: 1.55;
  color: #4b5563;
}

.layout-review-groups {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.layout-review-group {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.layout-review-group-title {
  margin: 0;
  font-size: 0.88rem;
  font-weight: 700;
  color: #111827;
}

.layout-review-item {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
  padding: 0.9rem;
  border: 1px solid #ece9e3;
  border-radius: 14px;
  background: linear-gradient(180deg, #ffffff 0%, #fbfbf8 100%);
}

.layout-review-item-header {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.layout-review-item-header strong {
  font-size: 0.87rem;
  color: #111827;
}

.layout-review-item-header span {
  font-size: 0.75rem;
  color: #9ca3af;
  word-break: break-all;
}

.layout-review-values {
  display: grid;
  grid-template-columns: 1fr;
  gap: 0.65rem;
}

.layout-review-value-block {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.layout-review-value-label {
  font-size: 0.72rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6b7280;
}

.layout-review-value {
  margin: 0;
  padding: 0.7rem 0.75rem;
  border-radius: 12px;
  background: #f7f7f5;
  border: 1px solid #ece9e3;
  white-space: pre-wrap;
  word-break: break-word;
  font-size: 0.78rem;
  color: #1f2937;
  font-family: inherit;
}

.layout-review-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.65rem;
  padding: 0.95rem 1rem 1rem;
  border-top: 1px solid rgba(0, 40, 100, 0.08);
}

@media (max-width: 1200px) {
  .layout-status-strip {
    grid-template-columns: 1fr;
  }

  .layout-status-actions {
    justify-content: flex-start;
  }
}

@media (max-width: 960px) {
  .layout-editor-shell,
  .layout-editor-shell--review-open {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .layout-review-drawer,
  .layout-review-drawer--open {
    width: 100%;
    max-width: none;
    opacity: 1;
    transform: none;
    border-color: #e8e6e0;
  }

  .layout-review-body {
    max-height: none;
  }

  .layout-review-backdrop {
    display: none;
  }
}

@media (max-width: 768px) {
  .layout-settings-grid {
    grid-template-columns: 1fr;
  }

  .layout-section-card {
    padding: 1rem;
  }

  .layout-setting-header {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>
